import { Box, Collapse, IconButton, Paper, Typography } from "@mui/material"
import { AddOutlined, KeyboardArrowDownOutlined, KeyboardArrowUpOutlined } from "@mui-symbols-material/w300"
import { useLoaderData, useParams, useSearch } from "@tanstack/react-router"
import type { FC } from "react"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useMutation } from "urql"

import { useProductOrigin } from "../hooks/useProductOrigin"

import { InformationMissingAlert } from "@/components/common/Alerts/InformationMissingAlert"
import { Button } from "@/components/common/Button"
import type { ProductOrigin } from "@/graphql/codegen/graphql"
import { ProductUpdateDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { ProductOriginDialog } from "@/screens/Products/components/Product/Information/Origin/ProductOriginDialog"
import { ProductOriginGrid } from "@/screens/Products/components/Product/Information/Origin/ProductOriginGrid"
import type { ProductOriginSubmissionType } from "@/screens/Products/components/Product/Information/types/productOrigin"
import { useNotificationsStore } from "@/stores/useNotificationsStore"
import { formatLocationAddress } from "@/utils/utils"

interface ProductOriginAlertProps {
  onAdd: () => void
  isPastProductVersion: boolean
}

const ProductOriginAlert: FC<ProductOriginAlertProps> = ({ onAdd, isPastProductVersion }) => {
  return (
    <InformationMissingAlert
      title='Add Product Origin Information'
      {...(!isPastProductVersion && {
        action: (
          <Button
            color='inherit'
            size='small'
            appearance='outlined'
            className='text-primary'
            startIcon={<AddOutlined />}
            onClick={onAdd}
            data-testid='add-origin-button'
          >
            Origin
          </Button>
        ),
      })}
      description=''
      data-testid='product-origin-alert'
    />
  )
}
interface ProductOriginContainerProps {
  isPastProductVersion: boolean
}

export const ProductOriginContainer: FC<ProductOriginContainerProps> = ({ isPastProductVersion }) => {
  const { productId } = useParams({ from: "/app/product/$productId/edit/_layout/information" })
  const [dialogOpen, setDialogOpen] = useState(false)
  const [expanded, setExpanded] = useState(true)

  const notify = useNotificationsStore(({ enqueueNotification }) => enqueueNotification)

  const [{ data: updateProductData, error: updateProductError, fetching: updateProductFetching }, updateProduct] =
    useMutation(ProductUpdateDocument)

  const { shippingLocations, companyLocations } = useLoaderData({
    from: "/app/product/$productId/edit/_layout/information",
  })
  const { version } = useSearch({ from: "/app/product/$productId/edit/_layout/information" })

  const { data: productOrigin, fetch: fetchProductOrigin } = useProductOrigin({ productId: Number(productId), version })

  const handleCloseOriginDialog = useCallback(() => {
    setDialogOpen(false)
  }, [])

  const handleOpenOriginDialog = () => {
    setDialogOpen(true)
  }

  const onDialogSubmit = useCallback(
    (data: {
      shippingOrigin: string
      manufacturingOrigin: ProductOriginSubmissionType
      countryOrigin: ProductOriginSubmissionType
    }) => {
      updateProduct({
        input: {
          productId: Number(productId),
          versionNumber: version,
          locationsAssociations: [{ locationAssociationId: data.shippingOrigin }],
          manufacturingOrigin: {
            exactLocationId: data.manufacturingOrigin.exactLocationId,
            countryIsoCode: data.manufacturingOrigin.countryIsoCode,
            regionIsoCode: data.manufacturingOrigin.regionIsoCode as string | null,
            city: data.manufacturingOrigin.city,
          },
          countryOrigin: {
            exactLocationId: data.countryOrigin.exactLocationId,
            countryIsoCode: data.countryOrigin.countryIsoCode,
            regionIsoCode: data.countryOrigin.regionIsoCode as string | null,
            city: data.countryOrigin.city,
          },
        },
      })
    },
    [productId, updateProduct, version]
  )

  useEffect(() => {
    if (!updateProductData) return

    if (!updateProductFetching && updateProductData?.product.update.__typename === "ProductUpdateSuccess") {
      fetchProductOrigin({ productId: Number(productId), version })
      notify({
        message: "Product origin updated successfully",
        type: NotificationType.Success,
      })
    }

    if (
      !updateProductFetching &&
      (updateProductData?.product.update.__typename === "ProductUpdateFailure" || updateProductError)
    ) {
      notify({
        message: "Error updating product origin",
        type: NotificationType.Error,
      })
    }
  }, [updateProductData, updateProductFetching, updateProductError, productId, fetchProductOrigin, notify, version])

  const hasOrigin = useMemo(
    () => (productOrigin?.locationsAssociations?.length ?? 0) > 0,
    [productOrigin?.locationsAssociations]
  )

  const getProductOriginLocationAddress = (origin: ProductOrigin) => {
    if (!origin?.exactLocationId) return null

    const exactLocation = origin.exactLocation
    // TODO: apply one of unified formats https://robotsandpencils.atlassian.net/browse/AK-2515
    return `${exactLocation?.description} - ${formatLocationAddress(exactLocation)}`
  }

  return (
    <>
      {dialogOpen && (
        <ProductOriginDialog
          onClose={handleCloseOriginDialog}
          onSubmit={onDialogSubmit}
          productOrigin={productOrigin}
          shippingOriginLocations={shippingLocations}
          companyManufacturingOriginLocations={companyLocations}
          data-testid='product-origin-dialog'
        />
      )}
      <Paper className='border border-gray-300 p-6' elevation={0} data-testid='product-origin-container'>
        <Box className='flex items-center justify-between'>
          <Typography variant='h6' className='mb-6' data-testid='product-origin-title'>
            Product Origin
          </Typography>
          <IconButton onClick={() => setExpanded(!expanded)} aria-expanded={expanded} aria-label='show more'>
            {expanded ? <KeyboardArrowUpOutlined /> : <KeyboardArrowDownOutlined />}
          </IconButton>
        </Box>
        <Collapse in={expanded}>
          {hasOrigin ? (
            <ProductOriginGrid
              isPastProductVersion={isPastProductVersion}
              shippingOrigin={{
                address: productOrigin?.locationsAssociations[0]?.shipping?.address1 ?? "",
                country: productOrigin?.locationsAssociations[0]?.shipping?.country?.name ?? "",
                city: productOrigin?.locationsAssociations[0]?.shipping?.city || "",
              }}
              manufacturingOrigin={{
                address: getProductOriginLocationAddress(productOrigin?.manufacturingOrigin as ProductOrigin),
                country: productOrigin?.manufacturingOrigin?.country?.name || "",
                region: productOrigin?.manufacturingOrigin?.region?.name || "",
                city: productOrigin?.manufacturingOrigin?.city || "",
              }}
              countryOrigin={{
                address: getProductOriginLocationAddress(productOrigin?.countryOrigin as ProductOrigin),
                country: productOrigin?.countryOrigin?.country?.name || "",
                region: productOrigin?.countryOrigin?.region?.name || "",
                city: productOrigin?.countryOrigin?.city || "",
              }}
              onEdit={handleOpenOriginDialog}
              data-testid='product-origin-grid'
            />
          ) : (
            <ProductOriginAlert onAdd={handleOpenOriginDialog} isPastProductVersion={isPastProductVersion} />
          )}
        </Collapse>
      </Paper>
    </>
  )
}
